import React from 'react';
import { CARD } from '../ui';

// Status is never color alone: each block carries an icon and a written label.
const TONES = {
  warning: { dot: 'bg-amber-400', text: 'text-amber-300', chip: 'bg-amber-500/10 text-amber-200 border-amber-500/25' },
  info: { dot: 'bg-sky-400', text: 'text-sky-300', chip: 'bg-sky-500/10 text-sky-200 border-sky-500/25' },
  neutral: { dot: 'bg-gray-400', text: 'text-gray-300', chip: 'bg-gray-600/20 text-gray-300 border-gray-600/40' },
};

const MAX_CHIPS = 6;

const Section = ({ tone, heading, hint, items }) => {
  if (!items.length) return null;
  const palette = TONES[tone];
  const shown = items.slice(0, MAX_CHIPS);
  const overflow = items.length - shown.length;

  return (
    <div className="py-4 border-b border-gray-700/60 last:border-b-0 first:pt-0 last:pb-0">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${palette.dot}`} aria-hidden="true" />
        <h3 className={`text-sm font-semibold ${palette.text}`}>
          {items.length} {heading}
        </h3>
      </div>
      {hint && <p className="text-xs text-gray-500 mt-1 ml-4">{hint}</p>}
      <div className="flex flex-wrap gap-1.5 mt-2 ml-4">
        {shown.map((item) => (
          <span
            key={item.key}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${palette.chip}`}
          >
            {item.label}
          </span>
        ))}
        {overflow > 0 && <span className="text-xs text-gray-500 self-center">+{overflow} more</span>}
      </div>
    </div>
  );
};

const AttentionPanel = ({ idleInspectors, pendingActivation, unusedForms, rangeLabel }) => {
  const total = idleInspectors.length + pendingActivation.length + unusedForms.length;

  return (
    <div className={`${CARD} p-6`}>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white">Needs attention</h2>
        <p className="text-sm text-gray-500 mt-1">Gaps worth a look for {rangeLabel}.</p>
      </div>

      {total === 0 ? (
        <div className="flex items-center gap-2 py-6 justify-center">
          <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" aria-hidden="true" />
          <p className="text-sm text-emerald-300">All clear — everyone submitted and every form was used.</p>
        </div>
      ) : (
        <div>
          <Section
            tone="warning"
            heading={`inspector${idleInspectors.length === 1 ? '' : 's'} with no submissions`}
            hint="Assigned to a supervisor but inactive over this period."
            items={idleInspectors}
          />
          <Section
            tone="info"
            heading={`account${pendingActivation.length === 1 ? '' : 's'} awaiting first login`}
            hint="Still on a one-time password, so they cannot submit yet."
            items={pendingActivation}
          />
          <Section
            tone="neutral"
            heading={`form${unusedForms.length === 1 ? '' : 's'} with no submissions`}
            hint="Published but unused over this period."
            items={unusedForms}
          />
        </div>
      )}
    </div>
  );
};

export default AttentionPanel;
